import { DateTime } from 'luxon'

import { FilterComparison } from '@/components/UI/Filter/utils/enums'
import type { Filter, FilterParam } from '@/components/UI/Filter/utils/types'

import { AnalyticsField } from './enums'

import { ASSET_FIELD, TAG_FIELD, UNKNOWN_VALUE } from '@/const'
import { escapeSqlString, formatSqlValue } from '@/helpers/common'

// prepare a filter condition for a single param, given a resolver from field
// name to the SQL expression that should be compared
export const getParamString = (prepareName: (field: string) => string) => (param: FilterParam) => {
  const fieldName = prepareName(param.field || '')
  const fieldValue = formatSqlValue(param.value)
  switch (param.comparison) {
    case FilterComparison.EQ:
      return `${fieldName} = ${fieldValue}`
    case FilterComparison.NOTEQ:
      return `${fieldName} <> ${fieldValue}`
    case FilterComparison.CONTAINS:
      return `lower(${fieldName}) LIKE '%' || lower(${fieldValue}) || '%'`
    case FilterComparison.NOTCONTAINS:
      return `lower(${fieldName}) NOT LIKE '%' || lower(${fieldValue}) || '%'`
    case FilterComparison.AFTER:
      return `${fieldName} > ${fieldValue}`
    case FilterComparison.BEFORE:
      return `${fieldName} < ${fieldValue}`
    case FilterComparison.RANGE:
      if (Array.isArray(param.value) && param.value.length === 2) {
        const start = String(param.value[0] ?? '')
        const end = String(param.value[1] ?? '')
        const escapedStart = escapeSqlString(start)
        const escapedEnd = escapeSqlString(end)
        return `${fieldName} BETWEEN '${escapedStart}' AND '${escapedEnd}'`
      }
      return `${fieldName} BETWEEN ${formatSqlValue(param.value)} AND ${formatSqlValue(param.value)}`
    case FilterComparison.LAST: {
      const start = DateTime.now().minus({ days: Number(param.value) }).toISODate()
      const end = DateTime.now().toISODate()
      const escapedStart = escapeSqlString(start || '')
      const escapedEnd = escapeSqlString(end || '')
      return `${fieldName} BETWEEN '${escapedStart}' AND '${escapedEnd}'`
    }
    case FilterComparison.GREATER:
      return `${fieldName} > ${fieldValue}`
    case FilterComparison.LESS:
      return `${fieldName} < ${fieldValue}`
    case FilterComparison.EMPTY:
      return `${fieldName} IS NULL`
    case FilterComparison.NOTEMPTY:
      return `${fieldName} IS NOT NULL`
  }
}

// entity-relation fields (`<prefix>_id` / `_name` / `_type`) resolved via a
// dedicated join alias so they don't collide with a getter's own `ast` join
const FILTER_OPTIONS_ENTITY_ACCESSORS: { prefix: string; idField: string; alias: string }[] = [
  { prefix: AnalyticsField.ACCOUNT, idField: AnalyticsField.ACCOUNT_ID, alias: 'flt_act' },
  { prefix: AnalyticsField.ASSET, idField: AnalyticsField.ASSET_ID, alias: 'flt_ast' },
  { prefix: AnalyticsField.CATEGORY_ACCOUNT, idField: AnalyticsField.CATEGORY_ACCOUNT_ID, alias: 'flt_cact' },
]

const resolveFilterOptionsField = (field: string, assetsTableName: string): { column: string; join?: string } => {
  for (const { prefix, idField, alias } of FILTER_OPTIONS_ENTITY_ACCESSORS) {
    if (field === idField) return { column: `tn.${field}` }
    if (field === `${prefix}_name` || field === `${prefix}_type`) {
      const valueField = field === `${prefix}_name` ? ASSET_FIELD.NAME : ASSET_FIELD.TYPE
      return {
        column: `${alias}.${valueField}`,
        join: `LEFT JOIN ${assetsTableName} ${alias} ON ${alias}.${ASSET_FIELD.ID} = tn.${idField}`,
      }
    }
  }
  return { column: `tn.${field}` }
}

// tags are stored as an array of tag ids on the row, so a tag param can't be
// compared as a plain column - it needs an EXISTS over the joined tags table
const getTagOptionsCondition = (param: FilterParam, tagsTableName: string): string | undefined => {
  const tagName = param.field || ''
  const existsClause = `SELECT 1 FROM UNNEST(tn.${AnalyticsField.TAGS}) AS u(tag_id) JOIN ${tagsTableName} tg ON tg.${TAG_FIELD.ID} = u.tag_id WHERE tg.${TAG_FIELD.TAG_NAME} = '${escapeSqlString(tagName)}'`
  const isUnknown = param.value === UNKNOWN_VALUE
  const valueMatch = `tg.${TAG_FIELD.TAG_VALUE} = ${formatSqlValue(param.value)}`
  const valueContains = `lower(tg.${TAG_FIELD.TAG_VALUE}) LIKE '%' || lower(${formatSqlValue(param.value)}) || '%'`
  switch (param.comparison) {
    case FilterComparison.EQ:
      return isUnknown
        ? `NOT EXISTS (${existsClause})`
        : `EXISTS (${existsClause} AND ${valueMatch})`
    case FilterComparison.NOTEQ:
      return isUnknown
        ? `EXISTS (${existsClause})`
        : `NOT EXISTS (${existsClause} AND ${valueMatch})`
    case FilterComparison.CONTAINS:
      return `EXISTS (${existsClause} AND ${valueContains})`
    case FilterComparison.NOTCONTAINS:
      return `NOT EXISTS (${existsClause} AND ${valueContains})`
    case FilterComparison.EMPTY:
      return `NOT EXISTS (${existsClause})`
    case FilterComparison.NOTEMPTY:
      return `EXISTS (${existsClause})`
    default:
      return undefined
  }
}

// turns the active filter (minus the param being edited) into extra
// WHERE/JOIN clauses so a dropdown's option list narrows to what the other
// active params already select
export const getAnalyticsFilterOptionsConditions = (
  filter: Filter | undefined,
  tagNames: string[] | undefined,
  assetsTableName: string,
  tagsTableName: string,
): { where: string; joins: string[] } | undefined => {
  if (!filter?.params?.length) return undefined
  const joins = new Set<string>()
  const groupClauses = filter.params
    .map(group => {
      const paramClauses = group.params
        .map(param => {
          if (!param.field || !param.comparison) return undefined
          if (tagNames?.includes(param.field)) {
            return getTagOptionsCondition(param, tagsTableName)
          }
          return getParamString(field => {
            const resolved = resolveFilterOptionsField(field, assetsTableName)
            if (resolved.join) joins.add(resolved.join)
            return resolved.column
          })(param)
        })
        .filter((clause): clause is string => !!clause)
      return paramClauses.length
        ? `(${paramClauses.join(` ${group.logic} `)})`
        : undefined
    })
    .filter((clause): clause is string => !!clause)
  if (!groupClauses.length) return undefined
  return {
    where: groupClauses.join(` ${filter.logic} `),
    joins: Array.from(joins),
  }
}

// SQL for "not equal" options: keep values where at least one narrowed row differs; uses IS DISTINCT FROM for NULL safety.
export const buildNotEqualOptionsQuery = (
  valueExpr: string,
  narrowedSource: string,
  domainSource: string,
) => `
      WITH r AS (SELECT ${valueExpr} AS value ${narrowedSource}),
      d AS (SELECT DISTINCT ${valueExpr} AS value ${domainSource})
      SELECT d.value AS value, d.value AS label
      FROM d
      WHERE EXISTS (SELECT 1 FROM r WHERE r.value IS DISTINCT FROM d.value)
      ORDER BY label
    `
